import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { globalHref, tenantHref } from '@/routing/tenant';
import { translateApiError } from '@/lib/apiError';
import { invitationRoleNames } from '@/lib/invitations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Field } from '@/components/synapse/Field';
import { Logo } from '@/components/synapse/Logo';
import { Stepper } from '@/components/synapse/Stepper';
import { Icon } from '@/components/synapse/Icon';
import { ThemeToggle } from '@/components/synapse/ThemeToggle';
import { LanguageToggle } from '@/components/synapse/LanguageToggle';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { Select } from '@/components/synapse/Select';
import {
  useAcceptMyInvitation,
  useCreateClinic,
  useCreateInvitation,
  useMyPendingInvitations,
} from '@/hooks/useClinics';
import { useClinicRoles } from '@/hooks/useRoles';
import { cn } from '@/lib/utils';

interface DetailsState {
  name: string;
  street: string;
  city: string;
  region: string;
  postcode: string;
  country: string;
  phone: string;
}

interface PendingInvite {
  email: string;
  roleId: string;
  roleName: string;
}

/**
 * Leave onboarding for the clinic the user now belongs to.
 *
 * This wizard is global — it exists precisely because the user had no clinic to
 * sit under — so finishing it always crosses into a different URL namespace,
 * which the router cannot do. A full page load is required, and is right: the
 * app has to remount with the new clinic's basename.
 */
function enterClinic(slug: string | null): void {
  window.location.replace(slug ? tenantHref(slug, '/') : globalHref('/'));
}

export function CreateDispensaryPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const createClinic = useCreateClinic();
  const myInvitesQuery = useMyPendingInvitations();
  const acceptInvite = useAcceptMyInvitation();
  const [step, setStep] = useState<1 | 2>(1);
  // The slug, not the id: it is both how this page addresses the new clinic's
  // API (see `useClinicRoles` below) and where it sends the user afterwards —
  // leaving this page means leaving the global namespace for the clinic's own.
  const [clinicSlug, setClinicSlug] = useState<string | null>(null);
  const [details, setDetails] = useState<DetailsState>({
    name: '',
    street: '',
    city: '',
    region: '',
    postcode: '',
    country: '',
    phone: '',
  });
  const [inviteEmail, setInviteEmail] = useState('');
  // Roles only exist once the clinic does, so this is empty on step 1 and
  // populated on step 2 — which is the only step that can invite anyone.
  // Addressed by SLUG, not by the URL: this page is global, so `clinicApi`
  // has no clinic to read. The clinic exists by step 2 — `clinicSlug` is set
  // from the create response.
  const { data: clinicRoles = [] } = useClinicRoles({
    enabled: Boolean(clinicSlug),
    clinicSlug: clinicSlug ?? undefined,
  });
  const [inviteRoleId, setInviteRoleId] = useState<string>('');
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);

  const createInvitation = useCreateInvitation(clinicSlug ?? undefined);

  const STEP_LABELS = useMemo(
    () => [t('dispensary.stepDetails'), t('dispensary.stepTeam')],
    [t],
  );

  async function onSubmitDetails(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    try {
      const res = await createClinic.mutateAsync({
        name: details.name.trim(),
        street: details.street.trim() || undefined,
        city: details.city.trim() || undefined,
        region: details.region.trim() || undefined,
        postcode: details.postcode.trim() || undefined,
        country: details.country.trim() || undefined,
        phone: details.phone.trim() || undefined,
      });
      setClinicSlug(res.clinic.slug);
      setStep(2);
    } catch (err) {
      setError(translateApiError(err, { fallback: t('dispensary.errCreateFailed') }));
    }
  }

  function addInvite() {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    if (pendingInvites.some((p) => p.email === email)) {
      setError(t('dispensary.errEmailListed'));
      return;
    }
    const role = clinicRoles.find((r) => r.id === inviteRoleId) ?? clinicRoles[0];
    if (!role) {
      setError(t('dispensary.errNoRoles'));
      return;
    }
    setPendingInvites((list) => [...list, { email, roleId: role.id, roleName: role.name }]);
    setInviteEmail('');
    setError(null);
  }

  function removeInvite(email: string) {
    setPendingInvites((list) => list.filter((p) => p.email !== email));
  }

  async function finish(skipInvites: boolean) {
    setError(null);
    setFinishing(true);
    try {
      const failures: Array<{ email: string; reason: string }> = [];
      if (!skipInvites && clinicSlug) {
        for (const invite of pendingInvites) {
          try {
            // One role from this wizard: the step offers a single picker, and
            // the fine-grained set is a decision better made from People once
            // the person is real. The API takes a list either way.
            await createInvitation.mutateAsync({
              email: invite.email,
              roleIds: [invite.roleId],
            });
          } catch (err) {
            failures.push({
              email: invite.email,
              reason: translateApiError(err, { fallback: t('dispensary.errUnknown') }),
            });
          }
        }
      }
      if (failures.length > 0) {
        setError(
          t('dispensary.errInvitesFailed', {
            details: failures.map((f) => `${f.email} (${f.reason})`).join('; '),
          }),
        );
        setPendingInvites(
          pendingInvites.filter((p) => failures.some((f) => f.email === p.email)),
        );
        return;
      }
      enterClinic(clinicSlug);
    } finally {
      setFinishing(false);
    }
  }

  function onCancel() {
    logout({ redirect: true });
  }

  async function onAcceptInvite(invitationId: string) {
    setError(null);
    try {
      const membership = await acceptInvite.mutateAsync(invitationId);
      enterClinic(membership.slug);
    } catch (err) {
      setError(translateApiError(err, { fallback: t('dispensary.errAcceptFailed') }));
    }
  }

  const invites = myInvitesQuery.data ?? [];

  const clinicInitials = (details.name || t('dispensary.yourDispensary'))
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');

  return (
    <div className="flex min-h-screen w-full flex-col bg-background text-foreground">
      <header className="flex h-14 shrink-0 items-center gap-4 border-b border-line bg-paper px-4 md:px-6">
        <Logo size={22} />
        <div className="hidden h-[22px] w-px bg-line md:block" />
        <span className="hidden text-[13px] text-muted md:inline">{t('dispensary.headerSetup')}</span>
        <div className="hidden flex-1 justify-center md:flex">
          <Stepper step={step} labels={STEP_LABELS} />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[12px] uppercase tracking-[0.04em] text-muted md:hidden">
            {t('dispensary.stepXOfY', { step, total: 2 })}
          </span>
          <ThemeToggle />
          <LanguageToggle />
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        </div>
      </header>

      <div className="border-b border-line bg-paper px-4 py-3 md:hidden">
        <Stepper step={step} labels={STEP_LABELS} compact />
      </div>

      <main className="flex flex-1 justify-center px-4 py-6 md:px-14 md:py-10">
        <div className="grid w-full max-w-[1100px] grid-cols-1 items-start gap-7 md:grid-cols-[1.6fr_1fr]">
          <section>
            <h1 className="text-[22px] font-semibold tracking-[-0.025em] md:text-[26px]">
              {step === 1 ? t('dispensary.detailsHeading') : t('dispensary.teamHeading')}
            </h1>
            <p className="mt-1.5 text-[13px] leading-[1.5] text-muted md:text-sm">
              {step === 1 ? t('dispensary.detailsSubtitle') : t('dispensary.teamSubtitle')}
            </p>

            {step === 1 && invites.length > 0 && (
              <div className="mt-5 flex flex-col gap-3">
                <div className="text-[12px] uppercase tracking-[0.06em] text-muted">
                  {t('dispensary.invitesWaiting')}
                </div>
                {invites.map((inv) => (
                  <Card key={inv.id} className="flex items-center gap-3 p-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ink text-[15px] font-bold text-accent-foreground">
                      {inv.clinicName
                        .trim()
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((s) => s[0]?.toUpperCase())
                        .join('')}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14px] font-semibold tracking-[-0.01em]">
                        {inv.clinicName}
                      </div>
                      <div className="text-[12px] text-muted">
                        {t('dispensary.roleOnJoin')}{' '}
                        <span className="text-ink-2">
                          {invitationRoleNames(inv.roles)}
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={() => onAcceptInvite(inv.id)}
                      disabled={acceptInvite.isPending}
                    >
                      {acceptInvite.isPending && acceptInvite.variables === inv.id
                        ? t('dispensary.joining')
                        : t('dispensary.acceptJoin')}
                    </Button>
                  </Card>
                ))}
                <div className="text-[11.5px] text-muted">{t('dispensary.orCreateBelow')}</div>
              </div>
            )}

            {step === 1 && myInvitesQuery.isFetched && invites.length === 0 && user && (
              <div className="mt-5 flex items-start gap-2.5 rounded-md border border-line bg-paper-2 px-3 py-2.5 text-[12.5px] text-ink-2">
                <Icon name="info" size={16} className="mt-0.5 shrink-0 text-muted" />
                <div>{t('dispensary.noInvitesFor', { email: user.email })}</div>
              </div>
            )}

            {step === 1 ? (
              <form onSubmit={onSubmitDetails} className="mt-5">
                <Card className="p-5 md:p-6">
                  <div className="flex flex-col gap-3.5">
                    <Field label={t('dispensary.nameLabel')} hint={t('dispensary.nameHint')}>
                      <Input
                        required
                        autoFocus
                        placeholder={t('dispensary.namePlaceholder')}
                        value={details.name}
                        onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))}
                      />
                    </Field>
                    <Field label={t('dispensary.street')} optional>
                      <Input
                        autoComplete="address-line1"
                        value={details.street}
                        onChange={(e) => setDetails((d) => ({ ...d, street: e.target.value }))}
                      />
                    </Field>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[1.6fr_1fr_1fr]">
                      <Field label={t('dispensary.city')} optional>
                        <Input
                          autoComplete="address-level2"
                          value={details.city}
                          onChange={(e) => setDetails((d) => ({ ...d, city: e.target.value }))}
                        />
                      </Field>
                      <Field label={t('dispensary.region')} optional>
                        <Input
                          autoComplete="address-level1"
                          value={details.region}
                          onChange={(e) => setDetails((d) => ({ ...d, region: e.target.value }))}
                        />
                      </Field>
                      <Field label={t('dispensary.postcode')} optional>
                        <Input
                          mono
                          autoComplete="postal-code"
                          value={details.postcode}
                          onChange={(e) => setDetails((d) => ({ ...d, postcode: e.target.value }))}
                        />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <Field label={t('dispensary.country')} optional>
                        <Input
                          autoComplete="country-name"
                          value={details.country}
                          onChange={(e) => setDetails((d) => ({ ...d, country: e.target.value }))}
                        />
                      </Field>
                      <Field label={t('dispensary.phone')} optional>
                        <Input
                          mono
                          autoComplete="tel"
                          value={details.phone}
                          onChange={(e) => setDetails((d) => ({ ...d, phone: e.target.value }))}
                        />
                      </Field>
                    </div>
                  </div>
                </Card>

                {error && (
                  <div className="mt-3 text-[12.5px] text-destructive" role="alert">
                    {error}
                  </div>
                )}

                <div className="mt-4 flex items-center gap-2.5">
                  <div className="flex-1" />
                  <Button
                    type="submit"
                    size="lg"
                    disabled={!details.name.trim() || createClinic.isPending}
                  >
                    {createClinic.isPending ? t('dispensary.saving') : t('dispensary.continueBtn')}
                    <Icon name="chevron_right" size={18} />
                  </Button>
                </div>
              </form>
            ) : (
              <div className="mt-5 flex flex-col gap-4">
                <Card className="p-5 md:p-6">
                  <div className="flex flex-col gap-3.5">
                    <div className="flex items-center gap-2.5 rounded-md bg-info-bg px-3 py-2.5 text-[12.5px] text-info">
                      <Icon name="info" size={16} />
                      <span>{t('dispensary.firstAdminNotice')}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_auto] md:items-end">
                      <Field label={t('dispensary.emailToInvite')}>
                        <Input
                          type="email"
                          placeholder={t('people.emailPlaceholder')}
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addInvite();
                            }
                          }}
                        />
                      </Field>
                      <Field label={t('people.role')}>
                        <Select
                          value={inviteRoleId || clinicRoles[0]?.id || ''}
                          onChange={(e) => setInviteRoleId(e.target.value)}
                        >
                          {clinicRoles.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Button type="button" variant="secondary" onClick={addInvite}>
                        <Icon name="plus" size={16} /> {t('dispensary.addInviteBtn')}
                      </Button>
                    </div>

                    {pendingInvites.length > 0 && (
                      <div>
                        <div className="mb-1.5 text-[12px] text-ink-2">
                          {t('dispensary.pendingInvites', { count: pendingInvites.length })}
                        </div>
                        <div className="rounded-md border border-line">
                          {pendingInvites.map((it, i) => (
                            <div
                              key={it.email}
                              className={cn(
                                'flex items-center gap-2.5 px-3.5 py-2.5',
                                i > 0 && 'border-t border-line',
                              )}
                            >
                              <InitialsAvatar
                                name={it.email.split('@')[0]?.replace('.', ' ') ?? ''}
                                size={28}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="mono truncate text-[12.5px]">{it.email}</div>
                                <div className="text-[11px] text-muted">
                                  {t('dispensary.willReceiveEmail')}
                                </div>
                              </div>
                              <span className="inline-flex h-[22px] items-center rounded-full bg-paper-3 px-2 text-[11.5px] font-medium text-ink-2">
                                {it.roleName}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeInvite(it.email)}
                                aria-label={`${t('common.remove')} ${it.email}`}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted hover:text-ink"
                              >
                                <Icon name="x" size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>

                {error && (
                  <div className="text-[12.5px] text-destructive" role="alert">
                    {error}
                  </div>
                )}

                <div className="flex items-center gap-2.5">
                  <Button variant="ghost" onClick={() => finish(true)} disabled={finishing}>
                    {t('dispensary.skipInviteLater')}
                  </Button>
                  <div className="flex-1" />
                  <Button onClick={() => finish(false)} disabled={finishing}>
                    <Icon name="check" size={16} />
                    {finishing ? t('dispensary.finishing') : t('dispensary.createBtn')}
                  </Button>
                </div>
              </div>
            )}
          </section>

          <aside className="hidden md:block">
            <div className="sticky top-6 flex flex-col gap-3.5 rounded-lg border border-line bg-paper p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-ink text-[18px] font-bold text-accent-foreground">
                  {clinicInitials || '–'}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold tracking-[-0.01em]">
                    {details.name || t('dispensary.yourDispensary')}
                  </div>
                  <div className="mono text-[11.5px] text-muted">
                    {step === 1 ? t('dispensary.inProgress') : t('dispensary.readyToLaunch')}
                  </div>
                </div>
              </div>
              <hr className="border-line" />
              <div className="text-[11px] uppercase tracking-[0.06em] text-muted">
                {t('dispensary.address')}
              </div>
              <div className="text-[12.5px] leading-[1.55]">
                {details.street ? (
                  <>
                    {details.street}
                    <br />
                    {[details.city, details.region, details.postcode].filter(Boolean).join(', ')}
                    {details.phone && (
                      <>
                        <br />
                        <span className="mono text-muted">{details.phone}</span>
                      </>
                    )}
                  </>
                ) : (
                  <span className="text-muted-2">—</span>
                )}
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.06em] text-muted">
                {t('dispensary.team')}
              </div>
              <div className="text-[12.5px]">
                {step === 2 && pendingInvites.length > 0
                  ? t('dispensary.oneAdminInvites', {
                      count: pendingInvites.length,
                      plural: pendingInvites.length === 1 ? '' : 's',
                    })
                  : t('dispensary.oneAdminYou')}
              </div>
              <hr className="border-line" />
              <p className="text-[11.5px] leading-[1.5] text-muted">{t('dispensary.settingsNote')}</p>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
